import React, { useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useAgentChat } from "../../hooks/useAgentChat";

export default function ChatAgentWidget({ auth }) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const messagesEndRef = useRef(null);

  const {
    messages,
    sendMessage,
    loading,
  } = useAgentChat({ workspaceId: localStorage.getItem("workspaceId"), token: auth.user?.id_token });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const handleSend = () => {
    if (!input.trim() || loading) return;
    sendMessage(input);
    setInput("");
  };

  const ui = !open ? (
    <button
      className="btn btn-primary rounded-circle position-fixed d-flex align-items-center justify-content-center"
      style={{ width: 60, height: 60, bottom: 20, right: 20, zIndex: 2000 }}
      onClick={() => setOpen(true)}
      aria-label="Open chat"
    >
      <MessageCircle />
    </button>
  ) : (
    <div
      className="position-fixed shadow-lg bg-white d-flex flex-column"
      style={{
        width: "30vw",
        height: "50vh",
        bottom: 20,
        right: 20,
        borderRadius: 12,
        zIndex: 2000
      }}
    >
      {/* Header */}
      <div
        className="d-flex align-items-center justify-content-between p-2 bg-primary text-white"
        style={{ borderTopLeftRadius: 12, borderTopRightRadius: 12 }}
      >
        <strong>Chat Agent</strong>
        <button className="btn btn-sm btn-light" onClick={() => setOpen(false)}>
          <X size={16} />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-grow-1 p-3 overflow-auto" style={{ background: "#ece5dd" }}>
        {messages.map((m, i) => (
          <div
            key={i}
            className={`d-flex mb-2 ${m.from === "user" ? "justify-content-end" : "justify-content-start"}`}
          >
            <div
              className={m.from === "user" ? "bg-success text-white px-3 py-2" : "bg-white px-3 py-2"}
              style={{ maxWidth: "75%", borderRadius: 16 }}
            >
              {m.text}
            </div>
          </div>
        ))}

        {loading && (
          <div className="d-flex justify-content-start mb-2">
            <div className="bg-white px-3 py-2 d-flex align-items-center gap-2" style={{ borderRadius: 16 }}>
              <Loader2 size={14} className="animate-spin" />
              <span className="text-muted small">Agent is typing…</span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-2 border-top d-flex gap-2">
        <input
          type="text"
          className="form-control"
          placeholder="Type a message"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSend()}
          disabled={loading}
        />
        <button className="btn btn-primary" onClick={handleSend} disabled={loading}>
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        </button>
      </div>
    </div>
  );

  return createPortal(ui, document.body);
}
